<template>
  <v-card
    class="action-card"
    :class="`action-card--${color}`"
    elevation="0"
    :style="{ animationDelay: `${delay}ms` }"
    @click="navigate"
  >
    <div class="card-content">
      <div class="icon-wrapper">
        <v-icon 
          :color="color" 
          size="48"
          class="action-icon"
        >
          {{ icon }}
        </v-icon>
      </div>
      
      <h3 class="action-title">{{ title }}</h3>
      <p class="action-description">{{ description }}</p>
      
      <div class="action-arrow">
        <v-icon color="grey lighten-1">mdi-arrow-right</v-icon>
      </div>
    </div>
    
    <div class="card-shine"></div>
  </v-card>
</template>

<script>
export default {
  name: 'ActionCard',
  
  props: {
    title: {
      type: String,
      required: true
    },
    description: {
      type: String,
      required: true
    },
    icon: {
      type: String,
      required: true
    },
    route: {
      type: String,
      required: true
    },
    color: {
      type: String,
      default: 'primary'
    },
    delay: {
      type: Number,
      default: 0
    }
  },
  
  methods: {
    navigate() {
      if (this.route.startsWith('#')) {
        // Handle anchor links or external documentation
        window.open(this.route, '_blank');
      } else {
        this.$router.push(this.route);
      }
    }
  }
};
</script>

<style scoped>
.action-card {
  height: 100%;
  cursor: pointer;
  border-radius: 16px;
  background: white;
  position: relative;
  overflow: hidden;
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
  border: 1px solid rgba(0, 0, 0, 0.05);
  animation: fadeInUp 0.6s ease-out forwards;
  opacity: 0;
  transform-style: preserve-3d;
  perspective: 1000px;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.card-content {
  padding: 32px;
  position: relative;
  z-index: 2;
}

.icon-wrapper {
  width: 80px;
  height: 80px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
  transition: all 0.3s ease;
  position: relative;
}

/* Color-specific gradients */
.action-card--primary .icon-wrapper {
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%);
}

.action-card--warning .icon-wrapper {
  background: linear-gradient(135deg, rgba(255, 152, 0, 0.1) 0%, rgba(255, 193, 7, 0.1) 100%);
}

.action-card--success .icon-wrapper {
  background: linear-gradient(135deg, rgba(76, 175, 80, 0.1) 0%, rgba(139, 195, 74, 0.1) 100%);
}

.action-card--info .icon-wrapper {
  background: linear-gradient(135deg, rgba(3, 169, 244, 0.1) 0%, rgba(0, 188, 212, 0.1) 100%);
}

.action-card--secondary .icon-wrapper {
  background: linear-gradient(135deg, rgba(156, 39, 176, 0.1) 0%, rgba(233, 30, 99, 0.1) 100%);
}

.action-card:hover .icon-wrapper {
  transform: scale(1.1) rotateZ(5deg);
}

.action-icon {
  transition: all 0.3s ease;
}

.action-card:hover .action-icon {
  transform: scale(1.2);
}

.action-title {
  font-size: 1.4rem;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 12px;
  transition: color 0.3s ease;
}

.action-description {
  font-size: 1rem;
  color: #546e7a;
  line-height: 1.5;
  margin-bottom: 0;
}

.action-arrow {
  position: absolute;
  bottom: 24px;
  right: 24px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.05);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  transform: translateX(0);
}

.action-card:hover .action-arrow {
  transform: translateX(8px);
  background: rgba(0, 0, 0, 0.1);
}

/* 3D hover effect */
.action-card:hover {
  transform: translateY(-8px) rotateX(-2deg) rotateY(2deg);
  box-shadow: 
    0 20px 40px rgba(0, 0, 0, 0.1),
    0 0 0 1px rgba(0, 0, 0, 0.05);
}

/* Shine effect */
.card-shine {
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: linear-gradient(
    45deg,
    transparent 30%,
    rgba(255, 255, 255, 0.3) 50%,
    transparent 70%
  );
  transform: rotate(45deg) translateX(-200%);
  transition: transform 0.6s ease;
}

.action-card:hover .card-shine {
  transform: rotate(45deg) translateX(200%);
}

/* Color-specific hover states */
.action-card--primary:hover {
  border-color: rgba(102, 126, 234, 0.3);
}

.action-card--primary:hover .action-title {
  color: #667eea;
}

.action-card--warning:hover {
  border-color: rgba(255, 152, 0, 0.3);
}

.action-card--warning:hover .action-title {
  color: #ff9800;
}

.action-card--success:hover {
  border-color: rgba(76, 175, 80, 0.3);
}

.action-card--success:hover .action-title {
  color: #4caf50;
}

.action-card--info:hover {
  border-color: rgba(3, 169, 244, 0.3);
}

.action-card--info:hover .action-title {
  color: #03a9f4;
}

.action-card--secondary:hover {
  border-color: rgba(156, 39, 176, 0.3);
}

.action-card--secondary:hover .action-title {
  color: #9c27b0;
}

@media (max-width: 600px) {
  .card-content {
    padding: 24px;
  }
  
  .action-title {
    font-size: 1.2rem;
  }
  
  .action-description {
    font-size: 0.9rem;
  }
  
  .icon-wrapper {
    width: 64px;
    height: 64px;
  }
}
</style> 